/**
 * Canvas-based lightmap texture generator for Ambient Occlusion.
 * Generates grayscale textures from AO data arrays.
 */
export default class AOTextureGenerator {
  // Default resolution for AO textures (texels per face dimension)
  static RESOLUTION = 8;

  // Reusable canvas for texture generation (performance optimization)
  static _canvas = null;
  static _ctx = null;

  /**
   * Get or create the shared canvas context.
   * @returns {CanvasRenderingContext2D} The 2D context
   */
  static getContext() {
    if (!this._canvas) {
      this._canvas = document.createElement('canvas');
      this._ctx = this._canvas.getContext('2d');
    }
    return this._ctx;
  }

  /**
   * Generate a grayscale lightmap texture as a data URL.
   * @param {Float32Array} aoData - AO values (0-1) for each texel, row-major order
   * @param {number} width - Texture width in texels
   * @param {number} height - Texture height in texels
   * @returns {string} Data URL of the generated texture
   */
  static generateTexture(aoData, width, height) {
    const ctx = this.getContext();

    // Resize canvas if needed
    if (this._canvas.width !== width || this._canvas.height !== height) {
      this._canvas.width = width;
      this._canvas.height = height;
    }

    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;

    for (let i = 0; i < aoData.length; i++) {
      // Clamp AO value to 0-1 range and convert to 0-255
      const ao = Math.max(0, Math.min(1, aoData[i]));
      const val = Math.floor(ao * 255);

      const idx = i * 4;
      data[idx] = val;       // R
      data[idx + 1] = val;   // G
      data[idx + 2] = val;   // B
      data[idx + 3] = 255;   // A (fully opaque)
    }

    ctx.putImageData(imageData, 0, 0);
    return this._canvas.toDataURL();
  }

  /**
   * Generate a texture with bilinear upscaling for smoother results.
   * Takes low-res AO data and upscales it for smoother gradients.
   * @param {Float32Array} aoData - AO values for each texel
   * @param {number} srcWidth - Source width in texels
   * @param {number} srcHeight - Source height in texels
   * @param {number} scale - Upscale factor (e.g., 2 = double resolution)
   * @returns {string} Data URL of the upscaled texture
   */
  static generateTextureUpscaled(aoData, srcWidth, srcHeight, scale = 2) {
    const dstWidth = srcWidth * scale;
    const dstHeight = srcHeight * scale;

    const ctx = this.getContext();

    // First, create source texture at original size
    if (this._canvas.width !== srcWidth || this._canvas.height !== srcHeight) {
      this._canvas.width = srcWidth;
      this._canvas.height = srcHeight;
    }

    const srcImageData = ctx.createImageData(srcWidth, srcHeight);
    const srcData = srcImageData.data;

    for (let i = 0; i < aoData.length; i++) {
      const ao = Math.max(0, Math.min(1, aoData[i]));
      const val = Math.floor(ao * 255);
      const idx = i * 4;
      srcData[idx] = val;
      srcData[idx + 1] = val;
      srcData[idx + 2] = val;
      srcData[idx + 3] = 255;
    }

    ctx.putImageData(srcImageData, 0, 0);

    // Create a second canvas for upscaling with smoothing
    const upscaleCanvas = document.createElement('canvas');
    upscaleCanvas.width = dstWidth;
    upscaleCanvas.height = dstHeight;
    const upscaleCtx = upscaleCanvas.getContext('2d');

    // Enable image smoothing for bilinear interpolation
    upscaleCtx.imageSmoothingEnabled = true;
    upscaleCtx.imageSmoothingQuality = 'high';

    // Draw scaled
    upscaleCtx.drawImage(this._canvas, 0, 0, dstWidth, dstHeight);

    return upscaleCanvas.toDataURL();
  }

  /**
   * Create a solid color texture (for testing or fallback).
   * @param {number} value - AO value 0-1 (0 = black, 1 = white)
   * @param {number} width - Texture width
   * @param {number} height - Texture height
   * @returns {string} Data URL
   */
  static generateSolidTexture(value, width = 1, height = 1) {
    const ctx = this.getContext();
    this._canvas.width = width;
    this._canvas.height = height;

    const val = Math.floor(Math.max(0, Math.min(1, value)) * 255);
    ctx.fillStyle = `rgb(${val},${val},${val})`;
    ctx.fillRect(0, 0, width, height);

    return this._canvas.toDataURL();
  }

  /**
   * Generate a debug gradient texture (for testing).
   * Creates a corner-darkened pattern.
   * @param {number} width - Texture width
   * @param {number} height - Texture height
   * @returns {string} Data URL
   */
  static generateDebugTexture(width = 8, height = 8) {
    const aoData = new Float32Array(width * height);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // Distance from center, normalized
        const cx = (x + 0.5) / width - 0.5;
        const cy = (y + 0.5) / height - 0.5;

        // Darker at corners, lighter at center
        const distFromCenter = Math.sqrt(cx * cx + cy * cy) * Math.SQRT2;
        const ao = 1 - (distFromCenter * 0.5);

        aoData[y * width + x] = ao;
      }
    }

    return this.generateTexture(aoData, width, height);
  }
}
